import { useEffect} from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { useAdminDashboard } from "../../../hooks/useAdminDashboard";
import { useStores } from "../../../hooks/useStores";
import { refetchDashboard, updateSelectedStore } from "../../../slices/dashboardSlice";
import ManagerLayout from "../Layout";
import type { Store } from "../../../types/store";
import DashboardDateFilter from "../../../components/DashboardDateFilter";
import { getComparisonLabel, type DatePreset } from "../../../utils/dateFilterUtils";
import StatCardSkeleton from "../../../components/StatCardSkeleton";
import StatCard from "../../../components/StatCard";
import ProfitMarginChartSkeleton from "../../../components/ProfitMarginChartSkeleton";
import ProfitMarginChart from "../../../components/ProfitMarginChart";
import RecentSalesSkeleton from "../../../components/RecentSalesTableSkeleton";
import RecentSalesTable from "../../../components/RecentSalesTable";
import TopSellingProductsSkeleton from "../../../components/TopProductsSkeleton";
import TopSellingProductsTable from "../../../components/TopSellingProductsTable";
import LowStockProductsTable from "../../../components/LowStockProductsTable";
import LowStockProductsSkeleton from "../../../components/LowStockProductsSkeleton";
import { useNavigate } from "react-router-dom";
import { selectOnboardingForRole, selectOnboardingStatus, fetchOnboarding } from "../../../slices/onboardingSlice";


const ManagerDashboard = () => {

const dispatch = useAppDispatch();
const navigate = useNavigate();

  const {
    overview,
    loading,
    initialized,
    profitMarginData,
    recentSales,
    topProducts,
    lowStockProducts
  } = useAdminDashboard();

  const { stores } = useStores();

  const selectedStore = useAppSelector(
    (state) => state.dashboard.selectedStore
  );

  const onboarding = useAppSelector(selectOnboardingForRole("Manager"));
  const onboardingStatus = useAppSelector(selectOnboardingStatus);

  useEffect(() => {
    dispatch(fetchOnboarding("Manager"));
  }, [dispatch]);

  useEffect(() => {
    if (onboardingStatus !== "succeeded") return;
    if (!onboarding.completedSteps.some(Boolean) && !onboarding.isSkipped) {
      navigate("/onboarding", { replace: true });
    }
  }, [onboardingStatus, onboarding.completedSteps, onboarding.isSkipped, navigate]);

  const dateFilter = useAppSelector((state) => state.dashboard.dateFilter);
  const { startDate: filterStart, endDate: filterEnd } = dateFilter;
  const sinceLabel = getComparisonLabel((dateFilter.preset as DatePreset | "custom" | null));

  useEffect(() => {
    dispatch(refetchDashboard());
  }, [selectedStore, filterStart, filterEnd]);

  const showResumeBanner =
    onboarding.isSkipped && !onboarding.completedSteps.every(Boolean);

  return (
    <ManagerLayout>
      <div className="p-4 space-y-6">

        {showResumeBanner && (
          <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 p-4">
            <div>
              <p className="font-medium text-gray-800">Continue setting up your workspace</p>
              <p className="text-sm text-gray-500">You haven't finished the onboarding wizard yet.</p>
            </div>
            <button
              onClick={() => navigate("/onboarding")}
              className="rounded bg-primary px-4 py-2 text-sm text-white hover:bg-primary/90"
            >
              Resume Setup
            </button>
          </div>
        )}

        <div className="flex flex-col gap-4 rounded-xl border bg-white/80 p-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <label className="text-sm font-medium text-gray-700 shrink-0">Filter by store</label>
            <select
              value={selectedStore?.id}
              onChange={(e) => {
                const storeId = e.target.value || null;

                const store =
                  storeId === null
                    ? null
                    : stores.find((store) => store.id === storeId) ?? null;

                dispatch(updateSelectedStore(store));
              }}
              className="form-input"
            >
              <option value="">All Stores</option>
              {stores.map((store: Store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Filter by date</p>
            <DashboardDateFilter />
          </div>
        </div>

        {/* ───────────── TOP METRICS ───────────── */}
        {
          loading.overview && !initialized.overview ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                title="Sales"
                value={overview?.sales.value as number}
                subtitle={overview?.sales.percentageChange as number}
                sinceLabel={sinceLabel}
              />
              <StatCard
                title="Revenue"
                value={`GHC ${overview?.revenue.value as number}`}
                subtitle={overview?.revenue.percentageChange as number}
                sinceLabel={sinceLabel}
              />
              <StatCard
                title="Customers"
                value={overview?.customers.value as number}
                subtitle={overview?.customers.percentageChange as number}
                sinceLabel={sinceLabel}
              />
            </div>
          )
        }

        {/* ───────────── PROFIT MARGIN CHART ───────────── */}
        <div className="rounded-xl bg-white/80 border p-4 shadow-sm">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-bold text-primary">Profit Margin Overview</h3>
            <span className="text-sm text-gray-500">This period</span>
          </div>
          {
            loading.profitMargin && !initialized.profitMargin ? (
              <ProfitMarginChartSkeleton />
            ) : (
              <ProfitMarginChart dataPoints={profitMarginData} />
            )
          }
        </div>

        {/* ───────────── RECENT SALES ───────────── */}
        {
          loading.recentSales && !initialized.recentSales ? (
            <RecentSalesSkeleton />
          ) : (
            <RecentSalesTable
              sales={recentSales}
              onViewAll={() => navigate("/history/sales")}
            />
          )
        }


        {/* ───────────── TOP PRODUCTS ───────────── */}
        {
          loading.topProducts && !initialized.topProducts ? (
            <TopSellingProductsSkeleton />
          ) : (
            <TopSellingProductsTable
              onViewAll={() => navigate("/history/products")}
              products={topProducts}
            />
          )
        }

        {/* ───────────── LOW STOCK PRODUCTS ───────────── */}
        {
          loading.lowStockProducts && !initialized.lowStockProducts ? (
            <LowStockProductsSkeleton />
          ) : (
            <LowStockProductsTable
              // onViewAll={() => navigate("/inventory")}
              products={lowStockProducts}
            />
          )
        }

        {/* ───────────── NOTIFICATIONS ───────────── */}
        {/* <div className="rounded-xl bg-white/80 border p-4 shadow-sm">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-bold text-primary">Notifications</h3>
            <button className="text-sm text-primary hover:underline">
              View all
            </button>
          </div>

          <NotificationsPanel notifications={mockNotifications} />
        </div> */}

      </div>
    </ManagerLayout>
  )};

export default ManagerDashboard;
